import React, { useRef, useEffect } from "react";
import { func, bool, string, number } from "prop-types";
import { Trans, useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";

import Divider from "@components/shared/Divider";
import SimpleButton from "@components/shared/SimpleButton";
import ErrorMessage from "@components/shared/ErrorMessage";
import Typography from "@components/shared/Typography";
import Address from "@components/shared/Address";
import StickyRequestButton from "@components/shared/StickyRequestButton";
import ButtonGroup from "@components/shared/ButtonGroup";
import LeadTimeTooltip from "@components/shared/LeadTimeTooltip";

import gtm from "@gtm/core";
import { useScrollToView } from "@hooks/useScrollToView";
import { addressType } from "@types";
import { revertProgressEvent } from "@gtm/events/shared";
import { basketDeliveryAddressUpdateUrlEvent } from "@gtm/events/basket";
import { STEPS } from "@gtm/constants";
import { useB2BDeliveryContactSelector } from "@redux/reducers/appConfig";
import { removeBasketErrorAction } from "@redux/actions/errorsActions";
import { postDealAction } from "@redux/actions/dealActions";

import {
  CTA_SOL_TYPES,
  DEAL_TYPE,
  DELIVERY_METHOD,
  REQUEST_ERROR_CODES,
} from "@shared/constants";

import {
  useDeliveryContactSelector,
  useDealDeliveryTypeSelector,
} from "@redux/reducers/deal";
import { useIsKeyAccount } from "@redux/reducers/auth";

const Confirmation = ({
  deliveryMethodLabel,
  affiliation,
  deliverySelected,
  onConfirmSuccess,
  onPrevState,
  onNextState,
  deliveryAddress,
  expectedDeliveryDate,
  errorMessage,
  primaryScreenErrorMessage,
  isHomeDelivery,
  pageVariant,
  isReadOnly,
  disableScroll,
  onModification,
  isOpen,
  isB2BSelected,
  isSSRRendered,
  fixedHeaderHeight,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef();
  const dispatch = useDispatch();
  const router = useRouter();
  const deliveryType = useDealDeliveryTypeSelector();
  const deliveryContact = useDeliveryContactSelector();
  const scrollToView = useScrollToView({ additionalOffset: 120 });

  const deliveryContactConfig = useB2BDeliveryContactSelector();
  const isKeyAccount = useIsKeyAccount();
  const deliveryContactEnabled = deliveryContactConfig.enabled || isKeyAccount;

  const isSalesmanPoint = deliveryType === DELIVERY_METHOD.SALESMAN_POINT;

  useEffect(() => {
    if (!disableScroll) {
      scrollToView(containerRef);
    }
    if (!isReadOnly && isOpen) {
      gtm.fire(
        basketDeliveryAddressUpdateUrlEvent({
          pageTitle: router.asPath,
          deliveryType,
          pageVariant,
          affiliation,
          isB2BSelected,
          isSSRRendered,
        })
      );
    }
  }, []);

  const handleOnModifyClick = () => {
    onModification?.();
    onNextState({ resetState: true });
    gtm.fire(revertProgressEvent(STEPS.DELIVERY));
  };

  const onContinueClick = async () => {
    await dispatch(
      postDealAction(CTA_SOL_TYPES.DELIVERY_POINT_SELECTED, DEAL_TYPE.DEAL)
    );

    onConfirmSuccess();

    return new Promise(() => null);
  };

  return (
    <div ref={containerRef}>
      <Typography
        className="font-semibold uppercase my-4"
        data-id="delivery-confirmation-title"
      >
        {t("basket.delivery.confirmationTitle")}
      </Typography>
      {isHomeDelivery ? (
        <Typography className="text-sm" data-id="delivery-label-confirmation">
          {deliveryMethodLabel}
        </Typography>
      ) : (
        <Address detail={deliveryAddress} />
      )}
      {expectedDeliveryDate && (
        <>
          <Divider className="my-4 bg-greyLight" />
          <Typography
            className="font-semibold uppercase my-4"
            data-id="delivery-date-title"
          >
            {t("basket.delivery.dateTitle")}
          </Typography>
          <Typography className="text-sm mb-2">
            {t("basket.orderSummary.leadDateInfo")}
          </Typography>
          <div className="flex">
            <Typography className="text-sm">{expectedDeliveryDate}</Typography>
            <LeadTimeTooltip id="delivery-confirmation-step" />
          </div>
        </>
      )}
      {isB2BSelected && deliveryContactEnabled && (
        <>
          <Divider className="my-4 bg-greyLight" />
          <Typography className="font-semibold my-4">
            {t("basket.delivery.deliveryContact.heading")}
          </Typography>
          <Typography className="text-sm">
            {deliveryContact
              ? t("basket.delivery.deliveryContact.used")
              : t("basket.delivery.deliveryContact.notUsed")}
          </Typography>
          <Typography className="text-sm mb-2">
            <Trans>
              {deliveryContact &&
                t("basket.delivery.deliveryContact.confirmation", {
                  deliveryContact,
                })}
            </Trans>
          </Typography>
        </>
      )}
      {!deliverySelected && (
        <>
          <div className="flex justify-between">
            <SimpleButton
              onClick={() => {
                onPrevState({ isSalesmanPoint });
                dispatch(
                  removeBasketErrorAction(
                    REQUEST_ERROR_CODES.DELIVERY_POINT_UNAVAILABLE
                  )
                );
                gtm.fire(revertProgressEvent(STEPS.DELIVERY));
              }}
              withArrow
              dataId="delivery-back-button"
            >
              {t("basket.back")}
            </SimpleButton>
          </div>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          {primaryScreenErrorMessage && (
            <ErrorMessage>{primaryScreenErrorMessage}</ErrorMessage>
          )}
          <ButtonGroup justifyCenter>
            <StickyRequestButton
              data-id="delivery-confirmation-continue-button"
              disabled={Boolean(primaryScreenErrorMessage)}
              fixedHeaderHeight={fixedHeaderHeight}
              isStickyToBottom={true}
              isStickyToTop={true}
              triggerRequestCallback={onContinueClick}
            >
              {t("basket.continue")}
            </StickyRequestButton>
          </ButtonGroup>
        </>
      )}
      {!isReadOnly && deliverySelected && (
        <>
          <SimpleButton
            dataId="basket-delivery-confirmation-modify-button"
            onClick={handleOnModifyClick}
            withArrow
          >
            {t("general.modify")}
          </SimpleButton>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </>
      )}
    </div>
  );
};

Confirmation.propTypes = {
  deliveryMethodLabel: string,
  deliverySelected: bool.isRequired,
  expectedDeliveryDate: string.isRequired,
  isHomeDelivery: bool.isRequired,
  pageVariant: string.isRequired,
  isB2BSelected: bool.isRequired,
  affiliation: string,
  deliveryAddress: addressType,
  onConfirmSuccess: func,
  onPrevState: func,
  onNextState: func,
  errorMessage: string,
  primaryScreenErrorMessage: string,
  isReadOnly: bool,
  disableScroll: bool,
  onModification: func,
  isOpen: bool,
  isSSRRendered: bool,
  fixedHeaderHeight: number,
};

export default Confirmation;
